// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import { getObjectKeys, NullPartial, omitKeys, pickKeys, subsetTypeHasKey, ValueOf } from "@hex-insights/core";
import { StudentCreateInput, StudentDetailQuery, StudentUpdateInput } from "../../../Utilities";
import { PersonFormConversion } from "../Person";
import { RelationshipFormConversion } from "../Relationship";
import { StudentAcademicTermEnrollmentFormConversion } from "../StudentAcademicTermEnrollment";
import { StudentCourseSectionEnrollmentFormConversion } from "../StudentCourseSectionEnrollment";
import { StudentHomeRoomSectionEnrollmentFormConversion } from "../StudentHomeRoomSectionEnrollment";
import { StudentMedicalConditionFormConversion } from "../StudentMedicalCondition";
import { StudentMedicalContactFormConversion } from "../StudentMedicalContact";
import { StudentMedicalEventFormConversion } from "../StudentMedicalEvent";
import { StudentMedicalProfileFormConversion } from "../StudentMedicalProfile";
import { StudentOTCMedicationFormConversion } from "../StudentOTCMedication";
import { StudentPrescriptionMedicationFormConversion } from "../StudentPrescriptionMedication";
import { StudentVaccinationRecordFormConversion } from "../StudentVaccinationRecord";
import * as StudentFormValues from "./form-values";

/**
 * Converts the given `formValues` for the create form to a GraphQL create input for the Student model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInput<K extends keyof StudentFormValues.Create = keyof StudentFormValues.Create>(
	formValues: StudentFormValues.CreateSubset<K>,
): StudentCreateInput {
	const inputFormValues = omitKeys(formValues, [
		"person",
		"relationships",
		"studentAcademicTermEnrollments",
		"studentCourseSectionEnrollments",
		"studentHomeRoomSectionEnrollments",
		"studentMedicalConditions",
		"studentMedicalContacts",
		"studentMedicalEvents",
		"studentMedicalProfile",
		"studentOtcMedications",
		"studentPrescriptionMedications",
		"studentVaccinationRecords",
	]);
	const input: NullPartial<StudentCreateInput> = {
		...inputFormValues,
		person:
			subsetTypeHasKey(formValues, "person") && formValues.person.length > 0
				? PersonFormConversion.toGQLCreateInStudentInput(formValues.person[0])
				: undefined,
		relationships: subsetTypeHasKey(formValues, "relationships")
			? formValues.relationships.map((e) => RelationshipFormConversion.toGQLCreateInStudentInput(e))
			: undefined,
		studentAcademicTermEnrollments: subsetTypeHasKey(formValues, "studentAcademicTermEnrollments")
			? formValues.studentAcademicTermEnrollments.map((e) =>
					StudentAcademicTermEnrollmentFormConversion.toGQLCreateInStudentInput(e),
			  )
			: undefined,
		studentCourseSectionEnrollments: subsetTypeHasKey(formValues, "studentCourseSectionEnrollments")
			? formValues.studentCourseSectionEnrollments.map((e) =>
					StudentCourseSectionEnrollmentFormConversion.toGQLCreateInStudentInput(e),
			  )
			: undefined,
		studentHomeRoomSectionEnrollments: subsetTypeHasKey(formValues, "studentHomeRoomSectionEnrollments")
			? formValues.studentHomeRoomSectionEnrollments.map((e) =>
					StudentHomeRoomSectionEnrollmentFormConversion.toGQLCreateInStudentInput(e),
			  )
			: undefined,
		studentMedicalConditions: subsetTypeHasKey(formValues, "studentMedicalConditions")
			? formValues.studentMedicalConditions.map((e) =>
					StudentMedicalConditionFormConversion.toGQLCreateInStudentInput(e),
			  )
			: undefined,
		studentMedicalContacts: subsetTypeHasKey(formValues, "studentMedicalContacts")
			? formValues.studentMedicalContacts.map((e) => StudentMedicalContactFormConversion.toGQLCreateInStudentInput(e))
			: undefined,
		studentMedicalEvents: subsetTypeHasKey(formValues, "studentMedicalEvents")
			? formValues.studentMedicalEvents.map((e) => StudentMedicalEventFormConversion.toGQLCreateInStudentInput(e))
			: undefined,
		studentMedicalProfile:
			subsetTypeHasKey(formValues, "studentMedicalProfile") && formValues.studentMedicalProfile.length > 0
				? StudentMedicalProfileFormConversion.toGQLCreateInStudentInput(formValues.studentMedicalProfile[0])
				: undefined,
		studentOtcMedications: subsetTypeHasKey(formValues, "studentOtcMedications")
			? formValues.studentOtcMedications.map((e) => StudentOTCMedicationFormConversion.toGQLCreateInStudentInput(e))
			: undefined,
		studentPrescriptionMedications: subsetTypeHasKey(formValues, "studentPrescriptionMedications")
			? formValues.studentPrescriptionMedications.map((e) =>
					StudentPrescriptionMedicationFormConversion.toGQLCreateInStudentInput(e),
			  )
			: undefined,
		studentVaccinationRecords: subsetTypeHasKey(formValues, "studentVaccinationRecords")
			? formValues.studentVaccinationRecords.map((e) =>
					StudentVaccinationRecordFormConversion.toGQLCreateInStudentInput(e),
			  )
			: undefined,
	};
	return input as StudentCreateInput;
}

/**
 * Converts the given `formValues` for the detail form to a GraphQL update input for the Student model.
 *
 * @param formValues The form values to convert. Generally only the changed form values.
 * @param initialFormValues The initial values of the form.
 */
export function toGQLUpdateInput<K extends keyof StudentFormValues.Detail = keyof StudentFormValues.Detail>(
	formValues: Partial<StudentFormValues.DetailSubset<K>>,
	_initialFormValues?: StudentFormValues.DetailSubset<K>,
): StudentUpdateInput {
	const inputFormValues = omitKeys(formValues, ["yearsFromGraduation"]);
	const input: StudentUpdateInput = { ...inputFormValues };

	if (subsetTypeHasKey(formValues, "graduationYear") && formValues.graduationYear === null) {
		input.clearGraduationYear = true;
	}

	return input;
}

export type StudentFormKeyMap = {
	graduationYear: "graduationYear";
	yearsFromGraduation: "yearsFromGraduation";
	personID: "person";
};

export type ToFormValuesArgs<K extends keyof StudentFormValues.Detail = keyof StudentFormValues.Detail> = {
	student: Pick<StudentDetailQuery["student"], ValueOf<Pick<StudentFormKeyMap, K>>>;
	fields?: { [Key in K]: true };
};

/**
 * Converts a Student instance to its corresponding detail form values.
 *
 * @param student The instance to convert.
 * @param fields The subset of fields to include.
 */
export function toFormValues<K extends keyof StudentFormValues.Detail = keyof StudentFormValues.Detail>({
	student,
	fields,
}: ToFormValuesArgs<K>): StudentFormValues.DetailSubset<K> {
	const formValues = {
		graduationYear: subsetTypeHasKey(student, "graduationYear") ? student.graduationYear : null,
		yearsFromGraduation: subsetTypeHasKey(student, "yearsFromGraduation") ? student.yearsFromGraduation : null,
		personID: subsetTypeHasKey(student, "person") ? student.person?.id ?? null : null,
	};
	return fields ? pickKeys(formValues, getObjectKeys(fields)) : formValues;
}
